import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, InputGroup, Row, Col, FloatingLabel, Alert, Spinner } from 'react-bootstrap';
import { FiSave, FiX, FiCalendar, FiClock, FiMapPin, FiGlobe, FiAlertTriangle } from 'react-icons/fi';
import api from '../services/api';

const TrainingFormModal = ({ show, onHide, initialData, mode, onSuccess, rooms }) => {
    const [formData, setFormData] = useState({
        titulo: '',
        descricao: '',
        date: '',
        time: '',
        local: '',
        sala_id: '',
        duracao: '',
        link_online: '',
        obriga_checkout: false
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [touched, setTouched] = useState({});

    // Reset form when modal opens
    useEffect(() => {
        if (show && initialData) {
            setFormData({
                titulo: initialData.titulo || '',
                descricao: initialData.descricao || '',
                date: initialData.date || '',
                time: initialData.time || '',
                local: initialData.local || '',
                sala_id: initialData.sala_id || '',
                duracao: initialData.duracao || '1',
                link_online: initialData.link_online || '',
                obriga_checkout: initialData.obriga_checkout || false
            });
            setError('');
            setTouched({});
        }
    }, [show, initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
        
        // Auto-fill local based on room selection
        if (name === 'sala_id') {
            const selectedRoom = rooms.find(r => r.id === parseInt(value));
            if (selectedRoom) {
                setFormData(prev => ({ ...prev, local: selectedRoom.nome }));
            }
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Basic Validation
        if (!formData.titulo || !formData.date || !formData.time || !formData.duracao) {
            setError('Preencha os campos obrigatórios.');
            setTouched({ titulo: true, date: true, time: true, duracao: true });
            return;
        }

        setLoading(true);
        setError('');

        try {
            const dateTime = `${formData.date}T${formData.time}:00`;
            const payload = {
                ...formData,
                data: dateTime,
                sala_id: formData.sala_id || null,
                participantes: [] // Default empty
            };

            if (mode === 'edit' && initialData.id) {
                await api.put(`/trainings/${initialData.id}`, payload);
            } else {
                await api.post('/trainings', payload);
            }

            onSuccess();
            onHide();
        } catch (err) {
            console.error(err);
            const msg = err.response?.data?.msg || 'Erro ao salvar treinamento.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static" centered className="training-modal-compact">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold text-primary">
                    {mode === 'edit' ? 'Editar Treinamento' : 'Novo Treinamento'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="d-flex align-items-center py-2 mb-3 small">
                        <FiAlertTriangle className="me-2" />
                        <div>{error}</div>
                    </Alert>
                )}
                
                <Form onSubmit={handleSubmit} id="trainingForm">
                    <Form.Group className="mb-3">
                        <FloatingLabel controlId="floatingTitulo" label="Título *">
                            <Form.Control 
                                type="text" 
                                name="titulo"
                                value={formData.titulo} 
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Título"
                                isInvalid={touched.titulo && !formData.titulo}
                            />
                        </FloatingLabel>
                    </Form.Group>

                    <Row className="g-2 mb-3">
                        <Col xs={7}>
                            <InputGroup>
                                <InputGroup.Text className="bg-light border-end-0"><FiCalendar /></InputGroup.Text>
                                <Form.Control 
                                    type="date" 
                                    name="date"
                                    value={formData.date} 
                                    onChange={handleChange}
                                    className="border-start-0"
                                    required
                                />
                            </InputGroup>
                        </Col>
                        <Col xs={5}>
                            <InputGroup>
                                <InputGroup.Text className="bg-light border-end-0"><FiClock /></InputGroup.Text>
                                <Form.Control 
                                    type="time" 
                                    name="time"
                                    value={formData.time} 
                                    onChange={handleChange}
                                    className="border-start-0"
                                    required
                                />
                            </InputGroup>
                        </Col>
                    </Row>

                    <Row className="g-2 mb-3">
                        <Col xs={4}>
                            <FloatingLabel controlId="floatingDuracao" label="Duração (h) *">
                                <Form.Control 
                                    type="number" 
                                    min="0.5"
                                    step="0.5"
                                    name="duracao"
                                    value={formData.duracao} 
                                    onChange={handleChange}
                                    placeholder="2"
                                />
                            </FloatingLabel>
                        </Col>
                        <Col xs={8}>
                            <FloatingLabel controlId="floatingSala" label="Sala (Opcional)">
                                <Form.Select 
                                    name="sala_id" 
                                    value={formData.sala_id} 
                                    onChange={handleChange}
                                >
                                    <option value="">Nenhuma</option>
                                    {rooms.map(room => (
                                        <option key={room.id} value={room.id}>{room.nome}</option>
                                    ))}
                                </Form.Select>
                            </FloatingLabel>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <InputGroup>
                            <InputGroup.Text className="bg-light border-end-0"><FiMapPin /></InputGroup.Text>
                            <Form.Control 
                                type="text" 
                                name="local"
                                value={formData.local} 
                                onChange={handleChange}
                                placeholder="Local presencial (opcional)"
                                className="border-start-0"
                            />
                        </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <InputGroup>
                            <InputGroup.Text className="bg-light border-end-0"><FiGlobe /></InputGroup.Text>
                            <Form.Control 
                                type="url" 
                                name="link_online"
                                value={formData.link_online} 
                                onChange={handleChange}
                                placeholder="Link online (opcional)"
                                className="border-start-0"
                            />
                        </InputGroup>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Control 
                            as="textarea" 
                            name="descricao"
                            value={formData.descricao} 
                            onChange={handleChange}
                            placeholder="Descrição (opcional)"
                            style={{ height: '80px', resize: 'none' }}
                            className="text-muted small"
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button variant="link" onClick={onHide} className="text-muted text-decoration-none">
                    Cancelar
                </Button>
                <Button variant="primary" type="submit" form="trainingForm" disabled={loading} className="px-4">
                    {loading ? <Spinner size="sm" animation="border" /> : <><FiSave className="me-2" /> Salvar</>}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TrainingFormModal;
